import { useEffect, useState } from "react";
import { Plus, Trash2, Crown, Repeat, UserPlus, Users, Search } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Panel, PageTitle } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { ManualRegister } from "@/components/ManualRegister";
import { RosterEdit } from "@/components/RosterEdit";
import { toast } from "sonner";

const RegistrationBadge = ({ team }) => {
  const reserve = team.registration_status === "reserve";

  return (
    <span
      className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-wider font-rajdhani border clip-beveled-sm ${
        reserve
          ? "border-orange-500/50 text-orange-300"
          : "border-emerald-500/50 text-emerald-300"
      }`}
      data-testid={`registration-status-${team.id}`}
    >
      {reserve
        ? `Riserva #${team.reserve_position || "?"}`
        : "Iscrizione attiva"}
    </span>
  );
};


export default function Squadre() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [rosterPlayers, setRosterPlayers] = useState({});
  const [tours, setTours] = useState([]);
  const [clans, setClans] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", tag: "", tournament_id: "", captain_id: "", player_ids: [] });
  const [manualTour, setManualTour] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickedId, setPickedId] = useState("");
  const [rosterTeam, setRosterTeam] = useState(null);
  const [search, setSearch] = useState("");
  const [filterTour, setFilterTour] = useState("");

  const load = () => {
    api.get("/teams").then(async ({ data }) => {
      setTeams(data);

      const rosters = await Promise.all(
        data.map(async (team) => {
          try {
            const { data: roster } = await api.get(`/teams/${team.id}/roster`);
            return [team.id, roster];
          } catch {
            return [team.id, []];
          }
        })
      );

      setRosterPlayers(Object.fromEntries(rosters));
    });
    api.get("/players").then(({ data }) => setPlayers(data));
    api.get("/tournaments", { params: { templates: false } }).then(({ data }) => setTours(data));
    api.get("/clans", { params: { active_only: true } }).then(({ data }) => setClans(data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/teams", { ...form, tournament_id: form.tournament_id || null, captain_id: form.captain_id || null });
      toast.success("Squadra creata"); setOpen(false);
      setForm({ name: "", tag: "", tournament_id: "", captain_id: "", player_ids: [] }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/teams/${id}`); toast.success("Eliminata"); load(); };

  const toggle = (pid) => setForm((f) => ({ ...f, player_ids: f.player_ids.includes(pid) ? f.player_ids.filter((x) => x !== pid) : [...f.player_ids, pid] }));
  const teamRosterPlayer = (teamId, id) =>
    (rosterPlayers[teamId] || []).find((player) => player.id === id);

  const pname = (id, teamId = null) => {
    const p =
      (teamId ? teamRosterPlayer(teamId, id) : null) ||
      players.find((player) => player.id === id);
    return p?.activision_id || p?.discord_username || "?";
  };

  const [subFor, setSubFor] = useState(null); // team id
  const [sub, setSub] = useState({ out_player_id: "", in_player_id: "", new_captain_id: "" });
  const available = players.filter((p) => !subFor || !teams.find((t) => t.id === subFor)?.player_ids.includes(p.id));

  const q = search.trim().toLowerCase();
  const filteredTeams = teams.filter((t) =>
    (!filterTour || t.tournament_id === filterTour) &&
    (!q || t.name.toLowerCase().includes(q) || (t.tag || "").toLowerCase().includes(q))
  );

  const doSubstitute = async () => {
    try {
      const payload = {};
      if (sub.out_player_id && sub.in_player_id) { payload.out_player_id = sub.out_player_id; payload.in_player_id = sub.in_player_id; }
      if (sub.new_captain_id) payload.new_captain_id = sub.new_captain_id;
      await api.put(`/teams/${subFor}/substitute`, payload);
      toast.success("Squadra aggiornata · KD ricalcolato");
      setSubFor(null); setSub({ out_player_id: "", in_player_id: "", new_captain_id: "" }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Squadre" subtitle="Roster & Capitani"
        action={canEdit ? <div className="flex gap-2 flex-wrap">
          <button data-testid="squadre-manual-register-btn" onClick={() => setShowPicker(true)} className="btn-outline-gold clip-beveled px-5 py-2.5 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Iscrivi Squadra</button>
          <button data-testid="new-team-btn" onClick={() => setOpen(!open)} className="btn-gold clip-beveled px-6 py-2.5 flex items-center gap-2"><Plus className="w-4 h-4" /> Nuova Squadra</button>
        </div> : null} />

      {canEdit && showPicker && (
        <Panel gold className="p-6 mb-6 boot-in" testid="tour-picker">
          <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)] mb-2">Seleziona il torneo per l'iscrizione manuale</p>
          <div className="flex gap-3 flex-wrap items-center">
            <select data-testid="picker-tour" value={pickedId} onChange={(e) => setPickedId(e.target.value)} className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
              <option value="">— Seleziona torneo —</option>{tours.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid="picker-continue" disabled={!pickedId} onClick={() => { setManualTour(tours.find((t) => t.id === pickedId)); setShowPicker(false); setPickedId(""); }} className="btn-gold clip-beveled px-5 py-2 disabled:opacity-40 disabled:cursor-not-allowed">Continua</button>
            <button onClick={() => setShowPicker(false)} className="btn-outline-gold clip-beveled px-5 py-2">Annulla</button>
          </div>
        </Panel>
      )}

      {canEdit && open && (
        <Panel gold className="p-6 mb-6 boot-in" testid="team-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Nome</label>
              <input data-testid="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" /></div>
            <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Tag</label>
              <input data-testid="team-tag" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" /></div>
            <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Torneo</label>
              <select value={form.tournament_id} onChange={(e) => setForm({ ...form, tournament_id: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none font-rajdhani">
                <option value="">— Nessuno —</option>{tours.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
            <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">Capitano</label>
              <select value={form.captain_id} onChange={(e) => setForm({ ...form, captain_id: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none font-rajdhani">
                <option value="">— Seleziona —</option>{form.player_ids.map((id) => <option key={id} value={id}>{pname(id)}</option>)}</select></div>
          </div>
          <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)] mt-4 mb-2">Roster (seleziona giocatori)</p>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {players.map((p) => (
              <button key={p.id} onClick={() => toggle(p.id)} className={`clip-beveled-sm px-3 py-1.5 text-xs font-rajdhani ${form.player_ids.includes(p.id) ? "bg-[var(--gold)] text-black" : "border border-[var(--steel)]/50 text-[var(--silver)]"}`}>
                {p.activision_id || p.discord_username || "Giocatore"} <span className="opacity-70">KD {p.kd}</span></button>
            ))}
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="save-team-btn" onClick={create} className="btn-gold clip-beveled px-6 py-2">Crea</button>
            <button onClick={() => setOpen(false)} className="btn-outline-gold clip-beveled px-6 py-2">Annulla</button>
          </div>
        </Panel>
      )}

      <div className="flex gap-3 flex-wrap items-center mb-4" data-testid="squadre-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[var(--silver)] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="team-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca squadra o tag…"
            className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm pl-9 pr-3 py-2 text-white font-rajdhani text-sm focus:border-[var(--gold)] focus:outline-none" />
        </div>
        <select data-testid="team-filter-tour" value={filterTour} onChange={(e) => setFilterTour(e.target.value)}
          className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani text-sm focus:border-[var(--gold)] focus:outline-none">
          <option value="">Tutti i tornei</option>{tours.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <span className="font-rajdhani text-xs text-[var(--silver)] uppercase tracking-wider" data-testid="team-count">{filteredTeams.length} squadre</span>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTeams.map((t) => (
          <Panel key={t.id} className="p-5" testid={`team-${t.id}`}>
            <div className="flex justify-between items-start">
              <div><h3 className="heading text-2xl text-white">{t.name}</h3>
              <div className="mt-1">
                <RegistrationBadge team={t} />
              </div>
                <span className="font-rajdhani text-[var(--gold)] text-xs">[{t.tag}]</span></div>
              {canEdit && (
                <button onClick={() => remove(t.id)} className="text-[var(--destructive)] p-1"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
            <div className="flex gap-4 mt-3 font-oswald">
              <div><div className="text-2xl font-bold text-[var(--gold)]">{t.avg_kd}</div><div className="font-rajdhani text-[10px] text-[var(--silver)] uppercase">KD Medio</div></div>
              <div><div className="text-2xl font-bold text-white">{t.total_kills}</div><div className="font-rajdhani text-[10px] text-[var(--silver)] uppercase">Kill</div></div>
              <div><div className="text-2xl font-bold text-white">{t.player_ids.length}</div><div className="font-rajdhani text-[10px] text-[var(--silver)] uppercase">Roster</div></div>
            </div>
            <div className="mt-3 space-y-1">
              {t.player_ids.map((id) => (
                <div key={id} className="font-rajdhani text-sm text-[var(--silver)] flex items-center gap-2">
                  {id === t.captain_id && <Crown className="w-3.5 h-3.5 text-[var(--gold)]" />}{pname(id, t.id)}</div>
              ))}
            </div>
            {canEdit && (
              <>
                <button data-testid={`substitute-btn-${t.id}`} onClick={() => { setSubFor(t.id); setSub({ out_player_id: "", in_player_id: "", new_captain_id: t.captain_id || "" }); }}
                  className="btn-outline-gold clip-beveled-sm w-full mt-3 py-1.5 text-xs flex items-center justify-center gap-1"><Repeat className="w-3 h-3" /> Sostituzioni</button>
                <button data-testid={`edit-roster-${t.id}`} onClick={() => setRosterTeam(t)}
                  className="btn-outline-gold clip-beveled-sm w-full mt-2 py-1.5 text-xs flex items-center justify-center gap-1"><Users className="w-3 h-3" /> Modifica Iscrizione</button>
              </>
            )}

            {canEdit && subFor === t.id && (
              <div className="mt-3 pt-3 border-t border-[var(--steel)]/30 boot-in" data-testid="substitute-form">
                <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)] mb-2">Sostituisci Giocatore</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <select data-testid="sub-out" value={sub.out_player_id} onChange={(e) => setSub({ ...sub, out_player_id: e.target.value })} className="bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1.5 text-xs text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
                    <option value="">Esce...</option>{t.player_ids.map((id) => <option key={id} value={id}>{pname(id, t.id)}</option>)}</select>
                  <select data-testid="sub-in" value={sub.in_player_id} onChange={(e) => setSub({ ...sub, in_player_id: e.target.value })} className="bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1.5 text-xs text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
                    <option value="">Entra...</option>{available.map((p) => <option key={p.id} value={p.id}>{p.activision_id || p.discord_username || "Giocatore"} (KD {p.kd})</option>)}</select>
                </div>
                <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)] mb-1">Capitano</p>
                <select data-testid="sub-captain" value={sub.new_captain_id} onChange={(e) => setSub({ ...sub, new_captain_id: e.target.value })} className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1.5 text-xs text-white font-rajdhani mb-2 focus:border-[var(--gold)] focus:outline-none">
                  {t.player_ids.map((id) => <option key={id} value={id}>{pname(id, t.id)}</option>)}</select>
                <div className="flex gap-2">
                  <button data-testid="save-substitute-btn" onClick={doSubstitute} className="btn-gold clip-beveled-sm flex-1 py-1.5 text-xs">Applica</button>
                  <button onClick={() => setSubFor(null)} className="btn-outline-gold clip-beveled-sm flex-1 py-1.5 text-xs">Annulla</button>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>

      {canEdit && manualTour && <ManualRegister tournament={manualTour} onClose={() => setManualTour(null)} onDone={load} />}
      {canEdit && rosterTeam && <RosterEdit team={rosterTeam} players={rosterPlayers[rosterTeam.id] || players} tournament={tours.find((t) => t.id === rosterTeam.tournament_id)} clans={clans} onClose={() => setRosterTeam(null)} onDone={load} />}
    </div>
  );
}
